import { useEffect, useRef, useState } from "react";

export const ORANGE = "var(--rb-orange)";
export const GRID = "var(--ad-hairline)";
export const TEXT3 = "var(--ad-fg-3)";

type SparkPoint = number | { value?: number; total?: number };

export function Sparkline({
  data,
  height = 40,
  fill = true,
  stroke = ORANGE,
}: {
  data: SparkPoint[];
  height?: number;
  fill?: boolean;
  stroke?: string;
}) {
  const W = 200;
  const H = height;
  const P = 2;
  const vals = data.map((d) =>
    typeof d === "number" ? d : d.value ?? d.total ?? 0,
  );
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = P + (i / (vals.length - 1)) * (W - 2 * P);
    const y = H - P - ((v - min) / span) * (H - 2 * P);
    return [x, y] as const;
  });
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  const area = `${d} L ${W - P} ${H - P} L ${P} ${H - P} Z`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
    >
      {fill && <path d={area} fill={stroke} opacity="0.14" />}
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
}

type AreaChartProps = {
  data: Array<Record<string, any>>;
  height?: number;
  valueKey?: string;
  labelKey?: string;
};

export function AreaChart({
  data,
  height = 240,
  valueKey = "total",
  labelKey = "label",
}: AreaChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(800);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(() => setW(ref.current!.clientWidth));
    ro.observe(ref.current);
    setW(ref.current.clientWidth);
    return () => ro.disconnect();
  }, []);
  const H = height;
  const padL = 40;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const vals = data.map((d) => d[valueKey] as number);
  const max = Math.max(...vals);
  const min = 0;
  const span = max - min || 1;
  const x = (i: number) =>
    padL + (i / Math.max(1, data.length - 1)) * innerW;
  const y = (v: number) => padT + innerH - ((v - min) / span) * innerH;
  const dPath = data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(d[valueKey])}`)
    .join(" ");
  const areaPath = `${dPath} L ${x(data.length - 1)} ${padT + innerH} L ${padL} ${padT + innerH} Z`;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => min + t * span);
  const xTickEvery = Math.max(1, Math.floor(data.length / 6));
  const [hover, setHover] = useState<number | null>(null);
  const onMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const idx = Math.round(((mx - padL) / innerW) * (data.length - 1));
    if (idx >= 0 && idx < data.length) setHover(idx);
    else setHover(null);
  };
  return (
    <div
      ref={ref}
      style={{ position: "relative", width: "100%", height: H }}
      onMouseLeave={() => setHover(null)}
      onMouseMove={onMove}
    >
      <svg width={W} height={H}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line
              x1={padL}
              y1={y(v)}
              x2={W - padR}
              y2={y(v)}
              stroke={GRID}
              strokeWidth="1"
            />
            <text
              x={padL - 8}
              y={y(v) + 3}
              textAnchor="end"
              fontSize="10"
              fill={TEXT3}
              fontFamily="var(--rb-font-mono)"
            >
              {Math.round(v).toLocaleString()}
            </text>
          </g>
        ))}
        <path d={areaPath} fill={ORANGE} opacity="0.12" />
        <path d={dPath} fill="none" stroke={ORANGE} strokeWidth="1.75" />
        {data.map(
          (d, i) =>
            i % xTickEvery === 0 && (
              <text
                key={i}
                x={x(i)}
                y={H - 8}
                textAnchor="middle"
                fontSize="10"
                fill={TEXT3}
              >
                {d[labelKey]}
              </text>
            ),
        )}
        {hover !== null && (
          <g>
            <line
              x1={x(hover)}
              y1={padT}
              x2={x(hover)}
              y2={padT + innerH}
              stroke={ORANGE}
              strokeOpacity="0.4"
            />
            <circle
              cx={x(hover)}
              cy={y(data[hover][valueKey])}
              r="4"
              fill={ORANGE}
            />
          </g>
        )}
      </svg>
      {hover !== null && (
        <div
          style={{
            position: "absolute",
            left: Math.min(W - 160, x(hover) + 8),
            top: y(data[hover][valueKey]) - 36,
            background: "var(--ad-surface-1)",
            border: "1px solid var(--ad-border-2)",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 11,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
          }}
        >
          <div style={{ color: "var(--ad-fg-3)", fontSize: 10 }}>
            {data[hover][labelKey]}
          </div>
          <div
            style={{
              color: "var(--ad-fg-1)",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {(data[hover][valueKey] as number).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

export function Donut({
  value,
  total,
  size = 140,
  label,
  sub,
}: {
  value: number;
  total: number;
  size?: number;
  label?: React.ReactNode;
  sub?: React.ReactNode;
}) {
  const pct = total === 0 ? 0 : value / total;
  const r = size / 2 - 10;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--ad-hairline)"
          strokeWidth="6"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={ORANGE}
          strokeWidth="6"
          strokeDasharray={`${c * pct} ${c}`}
          strokeLinecap="round"
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 500,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {label}
          </div>
          {sub && (
            <div
              style={{
                fontSize: 10,
                color: TEXT3,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              {sub}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function StackBar({
  items,
  height = 12,
}: {
  items: Array<{ label?: string; value: number; color?: string; opacity?: number }>;
  height?: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        height,
        borderRadius: 999,
        overflow: "hidden",
        background: "var(--ad-hairline)",
      }}
    >
      {items.map((it, i) => (
        <div
          key={i}
          title={`${it.label ?? ""} · ${(it.value * 100).toFixed(1)}%`}
          style={{
            width: `${it.value * 100}%`,
            background: it.color || ORANGE,
            opacity: it.opacity ?? 1,
            transition: "width 0.3s",
          }}
        />
      ))}
    </div>
  );
}
